import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { AUTH_LOGOUT } from 'actions/types'
import { HeaderMenu } from 'components/HeaderMenu'
import { LogoBlue } from 'components/Logo'
import { COMPANY_NAME, IS_MAIN_COMPANY } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { getInvestorPortalSections } from 'services/apis/participants'
import { convertUrl } from 'utils'

import { buildAccountMenu, renderNav } from '../utils'

/**
 * Participant portal header. The entries come from Admin > Configuration > Interface, from the
 * portal's own `participantPortal` section. It read `investorPortal` until now, which meant a
 * company could not label or order the two differently even though they are different people.
 *
 * A few of those entries only apply when the participant actually holds that side of the portal.
 * Offering Funds to someone with no fund position only ever answers 403, so those links stay
 * hidden until the section check says they apply. Anything else in the config is shown as saved.
 */
const SECTION_BY_LINK: Record<string, string[]> = {
  '/investorPortal/distributions': ['servicing', 'fund'],
  '/investorPortal/funds': ['fund'],
  '/investorPortal/purchases': ['origination'],
}

const MENU = [{ key: 'profile', label: 'My Profile', href: '/investorPortal/profile' }]

type NavItem = {
  name: string
  href: string
  visible: boolean
  order: number
  className?: string
}

export function ParticipantHeader() {
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [sections, setSections] = useState<Record<string, boolean> | null>(null)
  const [navigations, setNavigations] = useState<Record<string, NavItem>>({})
  const { auth, menu, accountMenu, logos } = useSelector((state: any) => ({
    auth: state.auth,
    menu: state.header.menu.participantPortal || {},
    accountMenu: state.header.menu.participantMenu || {},
    logos: state.header.menu.logos || {},
  }))

  // The dropdown, from the company's own `participantMenu` section; the row above is the default.
  const menus = useMemo(
    () => [...buildAccountMenu(MENU, accountMenu), { label: 'divider', href: '#', visible: true }],
    [accountMenu],
  )

  const location = useLocation()
  const navigate = useHistory()
  const dispatch = useDispatch()

  useEffect(() => {
    if (!auth.isAuthenticated) return
    getInvestorPortalSections()
      .then((res: any) => setSections(res || {}))
      .catch(() => setSections({}))
  }, [auth.isAuthenticated])

  useEffect(() => {
    const processed: Record<string, NavItem> = {}
    Object.entries(menu).forEach(([key, item]: [string, any]) => {
      if (!item) return
      processed[key] = {
        name: item.title || key,
        href: item.link || `/${key.toLowerCase()}`,
        visible: !!item.value,
        order: item.order ?? Object.keys(processed).length,
        ...(!!item.background && { className: 'bg-shade-blue rounded text-white hover:bg-white' }),
      }
    })
    setNavigations(
      Object.fromEntries(Object.entries(processed).sort(([, a], [, b]) => (a.order ?? Infinity) - (b.order ?? Infinity))),
    )
  }, [menu])

  const activeMenu = (to: string) => (location.pathname === to ? 'shadow' : '')

  const showItem = (key: string, item: NavItem) => {
    if (!item.visible || !menu[key]?.value) return false
    const required = SECTION_BY_LINK[item.href]
    if (!required) return true
    // Until the section check answers, keep the gated entries out so they don't flash on and off.
    if (!sections) return false
    return required.some((section) => !!sections[section])
  }

  const handleLogout = () => {
    dispatch({ type: AUTH_LOGOUT })
    navigate.push('/investorPortal/signin')
    window.location.reload()
  }

  const renderLogo = () => {
    const content = (
      <>
        <span className="sr-only">{COMPANY_NAME}</span>
        <LogoBlue />
      </>
    )
    const customLink = logos?.['participantPortal'] || logos?.['investorPortal']
    if (customLink) return <a href={convertUrl(customLink)}>{content}</a>
    return <Link to={IS_MAIN_COMPANY ? '/' : '/investorPortal'}>{content}</Link>
  }

  const navClass = (item: NavItem) =>
    `hover:shadow font-variation-settings-600 px-3 py-2 my-2 text-gray-600 hover:text-gray-900 ${activeMenu(item.href)} ${
      item.className || ''
    }`

  return (
    <div>
      <div className="max-w-screen-2xl m-auto relative py-4 px-2 bg-white">
        <nav className="relative flex items-center justify-between" aria-label="Global">
          <div className="flex items-center flex-grow flex-shrink-0 lg:flex-grow-0">
            <div className="flex items-center justify-between w-full md:w-auto">
              {renderLogo()}
              <div className="mr-2 flex items-center md:hidden">
                <div
                  className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                  onClick={() => setShowMobileMenu(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden flex-wrap items-center md:flex md:ml-10 md:pr-4 md:space-x-8">
            {Object.keys(navigations).map((key, index) => {
              const item = navigations[key]
              if (!showItem(key, item)) return null
              return renderNav(item, `nav-${index}`, navClass(item))
            })}
            {auth.isAuthenticated && (
              <HeaderMenu items={menus} onSelect={(href) => navigate.push(href)} onLogout={handleLogout} />
            )}
          </div>
        </nav>
      </div>

      {showMobileMenu && (
        <div>
          <div className="absolute z-10 top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden">
            <div className="rounded-lg shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
              <div className="px-5 pt-4 flex items-center justify-between">
                <div>
                  <LogoBlue />
                </div>
                <div className="-mr-2">
                  <div
                    className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span className="sr-only">Close main menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                </div>
              </div>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {Object.keys(navigations).map((key, index) => {
                  const item = navigations[key]
                  if (!showItem(key, item)) return null
                  return renderNav(
                    item,
                    `${index}`,
                    `block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50 ${activeMenu(
                      item.href,
                    )}`,
                  )
                })}
                {auth.isAuthenticated && (
                  <>
                    <div className="block rounded h-px bg-gray-100" />
                    {menus.map((item, index) =>
                      !item.visible || item.label === 'divider' ? null : (
                        <Link
                          key={`menu-${index}`}
                          to={item.href}
                          className="block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50"
                          onClick={() => setShowMobileMenu(false)}
                        >
                          {item.label}
                        </Link>
                      ),
                    )}
                    <div
                      className="block px-3 py-2 rounded-md text-base text-gray-600 font-bold hover:text-gray-900 hover:bg-gray-50"
                      onClick={handleLogout}
                    >
                      Sign Out
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
